import { gql } from '@apollo/client'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { Link } from 'gatsby'
import { toFriendlyKitId } from 'helpers/string'
import { useState } from 'react'
import ConfirmPhotoModal from './ConfirmPhotoModal'
import KitActions from './KitActions'
import KitHistory, { kitHistoryFragment } from './KitHistory'
import { KitDetailFragment } from './__generated__/KitDetailFragment'

const statusToExplanation = {
  CREATED: 'Kit ID reserved',
  REGISTERED: 'Email address verified',
  REGISTERED_UNVERIFIED: 'Email address not verified',
  LEGACY_ORDERED: 'Email address verified',
  COMPLETED: 'Completed, results sent',
}

export const kitDetailFragment = gql`
  fragment KitDetailFragment on Kit {
    id
    createdAt
    status
    sku
    registrationInfo {
      firstName
      lastName
      dob
      email
      passportNumber
      trackingNumber
    }
    smsRegistered
    mobileNumber
    day2and8ref
    order {
      id
      paymentStatus
      content {
        orderLines {
          sku {
            id
            price
            description
          }
          quantity
          unitPrice
          totalPrice
        }
      }
    }
    thirdPartyResults {
      associateId
      consentGiven
    }
    pendingPhotoVerification {
      id
      status
      versionId
      queuedTime
      queueName
      escalatedAt
      escalatedBy
      claimedAt
      claimedBy
      isHandled @client
      labEvent {
        labId
        firstName
        lastName
        dob
        rawData
        ingestDetails {
          filename
          csvRow
          ingestDate
        }
      }
    }

    ...KitHistoryFragment
  }

  ${kitHistoryFragment}
`

const PendingPhotoVerification = ({ kit }: { kit: KitDetailFragment }) => {
  const [showModal, setShowModal] = useState(false)
  const photo = kit.pendingPhotoVerification

  return (
    <>
      <div className="message is-warning mt-3">
        <div className="message-header">
          <p>Photo verification pending</p>
        </div>

        <div className="message-body">
          <strong>Waiting: </strong>
          {formatDistanceToNow(parseISO(photo.queuedTime))}

          {photo.queuedTime !== photo.labEvent.ingestDetails.ingestDate && (
            <>
              {' '}
              (Last registered{' '}
              {formatDistanceToNow(parseISO(photo.labEvent.ingestDetails.ingestDate), { addSuffix: true })})
            </>
          )}

          <div className="buttons is-right mt-3">
            <button className="button is-small is-info  is-fullwidth" onClick={() => setShowModal(true)}>
              Verify Photo
            </button>
          </div>
        </div>
      </div>
      {showModal && (
        <ConfirmPhotoModal
          onClose={() => setShowModal(false)}
          kitId={kit.id}
          sku={kit.sku}
          versionId={kit.pendingPhotoVerification.versionId}
          result={kit.pendingPhotoVerification}
        />
      )}
    </>
  )
}

const KitDetail = ({ kit }: { kit: KitDetailFragment }) => (
  <>
    <div className="columns">
      <div className="column">
        <h2 className="subtitle is-4">Kit {toFriendlyKitId(kit.id.replace('Kit-', ''))}</h2>

        <p>
          <strong>SKU: </strong>
          {kit.sku}
        </p>

        <p>
          <strong>Status:</strong> {kit.status} ({statusToExplanation[kit.status]})
        </p>

        {kit.registrationInfo && (
          <p>
            <strong>Reg details:</strong> {kit.registrationInfo.firstName.toUpperCase()}{' '}
            {kit.registrationInfo.lastName.toUpperCase()}, {kit.registrationInfo.dob}, {kit.registrationInfo.email}
          </p>
        )}

        {kit.registrationInfo?.passportNumber && (
          <p>
            <strong>Passport number:</strong> {kit.registrationInfo.passportNumber}
          </p>
        )}

        {kit.registrationInfo?.trackingNumber && (
          <p>
            <strong>Tracking number:</strong> {kit.registrationInfo.trackingNumber}
          </p>
        )}

        {kit.smsRegistered && (
          <p>
            <strong>SMS Registered: </strong> {kit.mobileNumber}
          </p>
        )}

        {kit.thirdPartyResults && (
          <>
            <p>
              <strong>Third party: </strong>
              <Link to={`/dashboard/third-party/${kit.thirdPartyResults.associateId}`}>
                {kit.thirdPartyResults.associateId}
              </Link>
            </p>

            {kit.thirdPartyResults.consentGiven !== null && (
              <p>
                <strong>Consent to share: </strong>
                {kit.thirdPartyResults.consentGiven ? (
                  <strong className="has-text-success">given</strong>
                ) : (
                  <strong className="has-text-danger">denied</strong>
                )}
                .
              </p>
            )}
          </>
        )}

        {kit.day2and8ref && (
          <p>
            <strong>Day 2 & 8 ref: </strong>
            {kit.day2and8ref}
          </p>
        )}

        {kit.pendingPhotoVerification && <PendingPhotoVerification kit={kit} />}

        <h3 className="subtitle is-4 mt-5">Actions</h3>

        <KitActions kit={kit} />

        <h3 className="subtitle is-4 mt-5">Order details</h3>

        {kit.order && <Link to={`/dashboard/order/${kit.order.id}`}>{kit.order.id}</Link>}

        <table className="table is-bordered">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Description</th>
              <th>Unit Price</th>
              <th>Quantity</th>
              <th>Total Price</th>
            </tr>
          </thead>
          <tbody>
            {kit.order ? (
              kit.order.content.orderLines.map((line, i) => (
                <tr key={i}>
                  <td>{line.sku.id}</td>
                  <td>{line.sku.description}</td>
                  <td>{line.unitPrice}</td>
                  <td>{line.quantity}</td>
                  <td>{line.totalPrice}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3}>No Order attached to this kit</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="column">
        <KitHistory kit={kit} />
      </div>
    </div>
  </>
)

export default KitDetail
